// Shared property-type catalog — one source of truth used by:
//   - /dashboard/properties (Add/Edit dropdown, list table)
//   - /dashboard (Low Cap-Rate Watchlist amber hint)
//
// `value` must match the backend `PropertyCreate.property_type` Literal.
// Cap-rate benchmarks are industry-typical ranges (late 2025 / early 2026 market).
// `emoji` is used per explicit user request for the dropdown + watchlist.

export const PROPERTY_TYPES = [
  { value: "condo",             label: "Condo",             emoji: "🏙️", capMin: 4,   capMax: 6    },
  { value: "duplex",            label: "Duplex",            emoji: "🏠", capMin: 5,   capMax: 7    },
  { value: "hotel",             label: "Hotel",             emoji: "🏨", capMin: 7.5, capMax: 10   },
  { value: "industrial",        label: "Industrial",        emoji: "🏭", capMin: 5.5, capMax: 7.5  },
  { value: "land",              label: "Land",              emoji: "🌿", capMin: null, capMax: null },
  { value: "manufactured_home", label: "Manufactured Home", emoji: "🚚", capMin: 7,   capMax: 10   },
  { value: "mixed_use",         label: "Mixed Use",         emoji: "🏙",  capMin: 6,   capMax: 7.5  },
  { value: "mobile_home",       label: "Mobile Home",       emoji: "🚐", capMin: 7,   capMax: 10   },
  { value: "motel",             label: "Motel",             emoji: "🛏️", capMin: 8,   capMax: 11   },
  { value: "multi_family",      label: "Multi-Family",      emoji: "🏘️", capMin: 4.5, capMax: 6.5  },
  { value: "office",            label: "Office",            emoji: "🏢", capMin: 6.5, capMax: 9    },
  { value: "other",             label: "Other",             emoji: "📦", capMin: null, capMax: null },
  { value: "quad_plex",         label: "Quad-Plex",         emoji: "🏘", capMin: 6,    capMax: 8    },
  { value: "retail",            label: "Retail",            emoji: "🏬", capMin: 6,   capMax: 8    },
  { value: "sfr",               label: "SFR",               emoji: "🏡", capMin: 4,   capMax: 6    },
  { value: "tiny_home",         label: "Tiny Home",         emoji: "🛖", capMin: 5,   capMax: 8    },
  { value: "townhouse",         label: "Townhouse",         emoji: "🏚️", capMin: 4,   capMax: 6    },
  { value: "warehouse",         label: "Warehouse",         emoji: "📦", capMin: 6,   capMax: 8    },
];

export const TYPE_BY_VALUE = Object.fromEntries(PROPERTY_TYPES.map(t => [t.value, t]));

let _benchmarksHydrated = false;

/**
 * Fetch live cap-rate benchmarks from the server and mutate the local catalog
 * in place. Called once at app boot from App.js. Safe to fail silently —
 * the frontend falls back to the hard-coded defaults above.
 */
export async function hydrateBenchmarks(api) {
  if (_benchmarksHydrated) return;
  try {
    const { data } = await api.get("/meta/property-benchmarks");
    if (!data || typeof data !== "object") return;
    for (const t of PROPERTY_TYPES) {
      const server = data[t.value];
      if (server && typeof server.min === "number" && typeof server.max === "number") {
        t.capMin = server.min;
        t.capMax = server.max;
      } else if (server === null) {
        t.capMin = null;
        t.capMax = null;
      }
    }
    _benchmarksHydrated = true;
  } catch (error) {
    console.warn("Benchmark hydration failed — falling back to baked defaults", error);
  }
}

/**
 * Compare a property's cap rate against its type-specific benchmark.
 * Returns { status: "below" | "in_range" | "above" | "unknown", min, max, label, emoji }
 */
export function benchmarkFor(type, cap) {
  const t = TYPE_BY_VALUE[type];
  if (!t) return { status: "unknown" };
  if (t.capMin == null) return { status: "unknown", label: t.label, emoji: t.emoji };
  if (typeof cap !== "number") return { status: "unknown", min: t.capMin, max: t.capMax, label: t.label, emoji: t.emoji };
  let status = "in_range";
  if (cap < t.capMin) status = "below";
  else if (cap > t.capMax) status = "above";
  return { status, min: t.capMin, max: t.capMax, label: t.label, emoji: t.emoji };
}
